// SideBarHeader.js
// This component renders a dynamic header for a sidebar with two modes: 'search' and default.
// In 'search' mode, it displays a Back button for navigation.
// In default mode, it displays a title ('Chats'), a notification bell toggle, and an Edit button for user actions.
// Props:
// - mode: Determines the rendering mode ('search' or default).
// - onBackClick: Callback function for the Back button.
// - onEditClick: Callback function for the Edit button.
// - onNotificationClick: Callback function for the notification bell icon.

import React from "react";
import PropTypes from "prop-types";
import { faAngleLeft, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import "../styles/SideBarHeader.css";
import { FaIcon } from "../shared/ui";
import { useMobileStyles } from "../hooks/useMobileStyles";
import { useNotificationActions } from "../hooks/useNotificationActions";
import NotificationBellIcon from "./shared/NotificationBellIcon";

function BackButton({ onClick, style }) {
  return (
    <div className="back-button" onClick={onClick}>
      <div className="icon">
        <FaIcon
          icon={faAngleLeft}
          style={style}
          size="lg"
        />
      </div>
      <span className="back-text title" style={style}>Back</span>
    </div>
  );
}

const EditButton = ({ onClick, style }) => (
  <FaIcon
    icon={faPenToSquare}
    className="edit-icon"
    style={style}
    onClick={onClick}
  />
);

const SideBarHeader = ({ mode, onBackClick = () => {}, onEditClick = () => {}, onNotificationClick = () => {} }) => {
  const { colorStyle, bgColorStyle } = useMobileStyles();
  
  // Use shared notification hook for visibility logic
  const { showNotificationIcon, hasInteractedWithNotifications, isNotificationsEnabled } = useNotificationActions();
  const canShowBell = hasInteractedWithNotifications();
  const notificationsEnabled = isNotificationsEnabled();

  return (
    <div
      className="sidebar-header"
      style={bgColorStyle || undefined}
    >
      <div className="sidebar-header-inner">
        {mode === "search" ? (
          <BackButton key="SideBarHeader_back-button" onClick={onBackClick} style={colorStyle || undefined} />
        ) : (
          <>
            <div key="SideBarHeader_title" className="title" style={colorStyle || undefined}>Chats</div>
            <div className="sidebar-header-actions">
              {showNotificationIcon && canShowBell && (
                <NotificationBellIcon 
                  key="SideBarHeader_notification-bell" 
                  onClick={onNotificationClick} 
                  style={colorStyle || undefined}
                  isEnabled={notificationsEnabled}
                />
              )}
              <EditButton key="SideBarHeader_edit-button" onClick={onEditClick} style={colorStyle || undefined} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

SideBarHeader.propTypes = {
  mode: PropTypes.string.isRequired,
  onBackClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onNotificationClick: PropTypes.func,
};

export default SideBarHeader;